/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useEffect, useState } from 'react'
import classNames from 'classnames'
import WistiaPlayer from 'react-player/wistia'
import Player from 'react-player'
import s from './style.module.css'

interface InlineVideoProps {
	appearance?: 'light' | 'dark'
	url: string
	description?: string
	solution?: 'infrastructure' | 'security' | 'networking' | 'applications'
	gradientPosition?: 'left' | 'right' | false
	videoClassName?: string
	aspectRatio?: string
	showAccent?: boolean
}

const DEFAULT_ASPECT_RATIO = '16 / 9'

function isWistiaHost(hostname: string): boolean {
	return /(^|\.)wistia\.(com|net)$/.test(hostname) || hostname === 'wi.st'
}

function isWistiaShareUrl(rawUrl: string): boolean {
	try {
		const parsedUrl = new URL(rawUrl)
		if (!isWistiaHost(parsedUrl.hostname)) {
			return false
		}

		return /^\/s\/([A-Za-z0-9_-]+)\/?$/.test(parsedUrl.pathname)
	} catch {
		return false
	}
}

function extractIframeSrcFromWistiaOEmbed(html: string): string | null {
	const match = html.match(/<iframe[^>]+src=\"([^\"]+)\"/i)
	return match?.[1] ?? null
}

const InlineVideo = (props: InlineVideoProps) => {
	const {
		appearance = 'light',
		gradientPosition = 'left',
		url,
		description,
		solution,
		videoClassName,
		aspectRatio,
		showAccent = true,
	} = props

	const hasAspectRatio = Boolean(aspectRatio)
	const [resolvedUrl, setResolvedUrl] = useState(url)

	const playerProps = {
		controls: true,
		url: resolvedUrl,
		width: '100%',
		height: '100%',
	}

	const [hasWindow, setHasWindow] = useState(false)

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setHasWindow(true)
		}
	}, [])

	useEffect(() => {
		let isActive = true

		async function resolveWistiaShareUrl() {
			if (!isWistiaShareUrl(url)) {
				if (isActive) {
					setResolvedUrl(url)
				}
				return
			}

			try {
				const oEmbedUrl = new URL('https://fast.wistia.com/oembed.json')
				oEmbedUrl.searchParams.set('url', url)

				const response = await fetch(oEmbedUrl.toString())
				if (!response.ok) {
					throw new Error('Could not resolve Wistia share URL')
				}

				const payload = (await response.json()) as { html?: string }
				const iframeSrc = payload.html
					? extractIframeSrcFromWistiaOEmbed(payload.html)
					: null

				if (isActive) {
					setResolvedUrl(iframeSrc || url)
				}
			} catch {
				if (isActive) {
					setResolvedUrl(url)
				}
			}
		}

		void resolveWistiaShareUrl()

		return () => {
			isActive = false
		}
	}, [url])

	return (
		<div
			className={classNames(
				s.inlineVideo,
				s[appearance],
				gradientPosition && s[gradientPosition],
			)}
			data-testid="wpl-inline-video"
		>
			<div
				className={classNames(s.videoContainer, solution && s[solution], {
					[s.hideAccentColor]: !showAccent,
				})}
				style={{
					['--aspect-ratio' as string]: hasAspectRatio
						? aspectRatio
						: DEFAULT_ASPECT_RATIO,
				}}
			>
				<div
					className={classNames(
						s.video,
						{
							[s.innerHasAspectRatio]: hasAspectRatio,
						},
						videoClassName,
					)}
				>
					{hasWindow ? (
						<>
							{resolvedUrl.includes('wistia') ? (
								<WistiaPlayer
									{...playerProps}
									config={{
										options: {
											controlsVisibleOnLoad: false,
										},
									}}
								/>
							) : (
								<Player {...playerProps} />
							)}
						</>
					) : null}
				</div>
			</div>

			{description ? <p className={s.description}>{description}</p> : null}
		</div>
	)
}

InlineVideo.displayName = 'InlineVideo'

export type { InlineVideoProps }
export { InlineVideo }
